/**
 * StudioShell — borderless portfolio / studio website feel.
 *  - Warm Cloud-tinted page background
 *  - Centered pill-shaped top nav floating over the content
 *  - Generous whitespace, no hard borders
 *  - Content container is narrower with breathing room
 *  - Pine + Sage accents
 */
import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { ProjectsNavDropdown } from '../components/layout/ProjectsNavDropdown.tsx';
import { UserMenu } from '../components/layout/UserMenu.tsx';

export function StudioShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  // Projects is rendered separately below (ProjectsNavDropdown) because
  // it has a hover-dropdown of recent projects; the rest are plain
  // pills. Order: Dashboard | New | (Editor, contextual) | Drafts | Projects.
  //
  // Admin intentionally does NOT live in the topline. It's tucked into
  // the UserMenu dropdown (only visible when user.isAdmin) so the
  // public nav stays the same shape for all users and the admin
  // affordance reads as a personal / account-scoped surface rather
  // than a product-level section.
  //
  // The "Editor" pill is CONTEXTUAL — it only appears while the user is
  // actively on a meeting-editor route, where it serves as a
  // "you are here" indicator. It used to live in the persistent nav as
  // a shortcut to the most-recently-updated meeting; that made the top
  // bar feel cluttered on pages where there was no open draft, so we
  // hide it elsewhere and rely on Drafts + project views as the
  // permanent entry points.
  const editorOpen =
    location.pathname.startsWith('/meetings/') &&
    location.pathname.endsWith('/edit');

  const navItems: {
    to: string;
    label: string;
    active: boolean;
  }[] = [
    { to: '/', label: 'Dashboard', active: location.pathname === '/' },
    {
      to: '/meetings/new',
      label: '+ New',
      active: location.pathname === '/meetings/new',
    },
    ...(editorOpen
      ? [
          {
            to: location.pathname,
            label: 'Editor',
            active: true,
          },
        ]
      : []),
    { to: '/drafts', label: 'Drafts', active: location.pathname === '/drafts' },
  ];
  const projectsActive =
    location.pathname === '/projects' || location.pathname.startsWith('/projects/');

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ backgroundColor: 'var(--color-page-bg)', color: 'var(--color-text)' }}
    >
      {/* Top strip: small logo + user on either side of a centered pill nav.
          relative + z-50 establishes a higher stacking context than <main>,
          which is needed because the dashboard hero card uses paper-grain
          (whose ::before noise overlay declares mix-blend-mode and so
          creates its OWN stacking context for the hero). Without this,
          the ProjectsNavDropdown panel rendered behind the hero. */}
      <header className="relative z-50 px-8 pt-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="flex items-center gap-3 no-underline">
            <img src="/ojb-logo-charcoal.png" alt="OJB" className="h-9 w-auto" />
            {/* Compact wordmark next to the logo — just the product name
                in tracked uppercase. Firm name was removed per pilot
                feedback that it felt redundant with the logo. */}
            <div
              className="text-[11px] font-semibold uppercase tracking-[0.25em]"
              style={{ color: 'var(--color-nav-accent)' }}
            >
              Meeting Minutes
            </div>
          </Link>

          {/* Pill-group nav — single bone-tinted container with
              backdrop-blur holding individual pills. Each pill is
              transparent idle, pine-soft on hover, full pine fill +
              cream text when active. The container itself drops the
              pine outline and sits on the page softly. */}
          <nav
            className="flex items-center gap-1 rounded-full border border-charcoal/5 bg-bone/65 p-1 text-sm backdrop-blur-md"
          >
            {navItems.map((item) => (
              <StudioNavLink
                key={item.label}
                to={item.to}
                active={item.active}
              >
                {item.label}
              </StudioNavLink>
            ))}
            {/* Projects slot — same pill styling, but with a hover
                dropdown listing the user's most-recent projects. */}
            <ProjectsNavDropdown active={projectsActive} />
          </nav>

          {/* Right-side affordance — avatar-pill dropdown with spend,
              style-profile viewer, and sign-out. Consolidated from
              the previous trio (name + email + outline button). */}
          {user && <UserMenu />}
        </div>
      </header>

      <main className="flex-1 pt-6">{children}</main>

      <footer
        className="mx-auto w-full max-w-6xl px-8 pb-8 pt-4 text-[11px]"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {/* Tri-color brand band removed per pilot direction — no
            longer part of the OJB brand standards for this app.
            See packages/brand/src/tokens.css for the deprecation
            note. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>
            OJB Landscape Architecture - Boston · Dallas · Denver · Houston · Philadelphia · San
            Diego
          </span>
          <span style={{ color: 'var(--color-nav-accent)' }}>ojb.com</span>
        </div>
      </footer>
    </div>
  );
}

function StudioNavLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: ReactNode;
}) {
  // Sits inside the pill-group container — drops its individual
  // border in favor of a transparent default, pine-soft hover tint,
  // and full pine fill when active.
  const base =
    'inline-block rounded-full px-4 py-1.5 text-sm font-medium transition-colors';
  const variant = active
    ? 'bg-pine text-cloud'
    : 'text-charcoal hover:bg-pine/10 hover:text-pine';
  return (
    <Link to={to} className={`${base} ${variant}`}>
      {children}
    </Link>
  );
}
